/**
 * Centralized error extraction and categorization for admin portal.
 */

const ERROR_CATEGORIES = {
  validation: { type: 'validation', icon: 'AlertTriangle', color: '#f59e0b', title: 'Validation Error' },
  permission: { type: 'permission', icon: 'Lock', color: '#dc2626', title: 'Access Denied' },
  notFound: { type: 'notFound', icon: 'Search', color: '#64748b', title: 'Not Found' },
  conflict: { type: 'conflict', icon: 'AlertCircle', color: '#f59e0b', title: 'Conflict' },
  network: { type: 'network', icon: 'WifiOff', color: '#dc2626', title: 'Connection Error' },
  server: { type: 'server', icon: 'ServerCrash', color: '#dc2626', title: 'Something Went Wrong' },
}

/**
 * Strip common exception type prefixes from server messages.
 * e.g. "System.InvalidOperationException: Actual message" → "Actual message"
 */
function stripExceptionPrefix(message) {
  if (!message || typeof message !== 'string') return message
  return message.replace(/^[\w.]+Exception:\s*/i, '')
}

/**
 * Format validation errors object into readable lines.
 * Handles ASP.NET ModelState format: { "Field": ["error1", "error2"] }
 */
function formatValidationErrors(errors) {
  if (!errors || typeof errors !== 'object') return null

  const lines = []
  for (const [field, messages] of Object.entries(errors)) {
    const msgList = Array.isArray(messages) ? messages : [messages]
    for (const msg of msgList) {
      if (typeof msg === 'string') {
        // Skip generic keys like '' or '$'
        if (field && field !== '' && field !== '$') {
          lines.push(`${field}: ${msg}`)
        } else {
          lines.push(msg)
        }
      }
    }
  }

  return lines.length > 0 ? lines.join('\n') : null
}

/**
 * Lowercase only the first character, leaving the rest of the word intact.
 * "RecipientEmail" → "recipientEmail" (not "recipientemail").
 */
function toCamelCase(key) {
  return key.charAt(0).toLowerCase() + key.slice(1)
}

/**
 * Map an ASP.NET validation errors dictionary onto a form's own field names.
 *
 * The server names a field in whatever casing produced the failure: a property
 * name for annotation errors ("RecipientEmail") or a JSON path for binding
 * errors ("$.deliveryType"). Forms read camelCase, so the raw key has to be
 * normalised before it can be matched to an input.
 *
 * Known fields win on a case-insensitive match and keep the form's own spelling.
 * Anything unrecognised is camel-cased rather than flattened, and still reaches
 * the operator through the generic modal, which formats the whole dictionary.
 *
 * @param {object} apiErrors - err.response.data.errors, { Field: ["message"] }
 * @param {string[]} knownFields - the field names the form owns
 * @returns {object} { fieldName: 'message' }
 */
export function mapApiFieldErrors(apiErrors, knownFields = []) {
  if (!apiErrors || typeof apiErrors !== 'object') return {}

  const byLowerCase = new Map(knownFields.map(field => [field.toLowerCase(), field]))
  const mapped = {}

  for (const [rawKey, messages] of Object.entries(apiErrors)) {
    // Body-binding failures arrive as a JSON path: "$.deliveryType".
    const key = rawKey.replace(/^\$\.?/, '')

    // '' and '$' are the model-level bucket, not a field.
    if (!key) continue

    const field = byLowerCase.get(key.toLowerCase()) ?? toCamelCase(key)
    const message = Array.isArray(messages) ? messages[0] : messages

    if (typeof message === 'string' && message) {
      mapped[field] = message
    }
  }

  return mapped
}

/**
 * Extract a human-readable error message from any error object.
 * Handles axios errors, plain Error objects, and string errors.
 */
export function extractErrorMessage(err) {
  if (!err) return 'Something went wrong. Please try again.'
  if (typeof err === 'string') return err

  const data = err.response?.data

  if (data) {
    // 1. Validation errors (ASP.NET ModelState)
    if (data.errors) {
      const formatted = formatValidationErrors(data.errors)
      if (formatted) return formatted
    }

    // 2. Problem Details: detail field
    if (data.detail && typeof data.detail === 'string') {
      return stripExceptionPrefix(data.detail)
    }

    // 3. Message field
    if (data.message && typeof data.message === 'string') {
      return stripExceptionPrefix(data.message)
    }

    // 4. Title field
    if (data.title && typeof data.title === 'string') {
      return stripExceptionPrefix(data.title)
    }

    // 5. Plain string response
    if (typeof data === 'string') {
      return stripExceptionPrefix(data)
    }
  }

  // 6. Axios / generic error message
  if (err.message && typeof err.message === 'string') {
    if (err.message === 'Network Error') {
      return 'Unable to connect to the server. Please check your internet connection.'
    }
    return err.message
  }

  return 'Something went wrong. Please try again.'
}

/**
 * Check if the error is a network error (no response from server).
 */
export function isNetworkError(err) {
  if (!err) return false
  // Axios: no response means network failure
  if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') return true
  if (err.code === 'ECONNABORTED') return true // timeout
  if (err.request && !err.response) return true
  return false
}

/**
 * Map an error to a category based on HTTP status code.
 */
export function categorizeError(err) {
  if (isNetworkError(err)) return ERROR_CATEGORIES.network

  const status = err.response?.status
  switch (status) {
    case 400: return ERROR_CATEGORIES.validation
    case 403: return ERROR_CATEGORIES.permission
    case 404: return ERROR_CATEGORIES.notFound
    case 409: return ERROR_CATEGORIES.conflict
    default:
      if (status >= 500) return ERROR_CATEGORIES.server
      return ERROR_CATEGORIES.server
  }
}
